// The four Approvals queues (PRD §7.3.4 /approvals: FR-A1 business verification,
// FR-A19 services, FR-A20 payment verification, FR-A17 claims — DR-39) mapped onto one
// row model, so the list, filters, SLA chips and bulk selection behave the same on
// every tab (design `approvalVals`, D:10298). Pure functions: the screen fetches, this
// file shapes, filters and sorts. Every label comes from packages/i18n.
import type { Lang } from '@bookingdei/i18n/translate';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import type { MessageParams } from '@bookingdei/i18n/interpolate';
import { formatDate } from '@bookingdei/i18n/format-date';
import { statusLabelKey } from '@bookingdei/i18n/status-label';
import type {
  AdminBusiness,
  AdminCatalogItem,
  AdminCertificate,
  AdminClaim,
  Category,
  LocationNode,
  PaymentKycState,
  Template,
} from '../../mock/seeds/schema-types';
import { APPROVAL_SLA } from './approvals-config';
import { phonePlan } from '../../theme/phone-plan';
import { formatPhone } from '@bookingdei/i18n/phone';

export type QueueTab = 'merchants' | 'listings' | 'payments' | 'claims' | 'certificates';
export const QUEUE_TABS: readonly QueueTab[] = ['merchants', 'listings', 'payments', 'claims', 'certificates'];
export type Decided = 'pending' | 'approved' | 'rejected' | 'all';
export const DECIDED: readonly Decided[] = ['pending', 'approved', 'rejected', 'all'];
export type SlaFilter = 'all' | 'due' | 'overdue';

/** Design row states: waiting (SLA live) · waiting_merchant (SLA paused) · decided · none (payments not applied). */
export type RowState = 'waiting' | 'waiting_merchant' | 'approved' | 'rejected' | 'none';
export type ChipTone = 'neutral' | 'warn' | 'ok' | 'danger' | 'info' | 'violet';

export interface RowChip {
  text: string;
  tone: ChipTone;
}

export interface QueueRow {
  id: string;
  tab: QueueTab;
  title: string;
  chips: RowChip[];
  meta: string;
  state: RowState;
  /** Whole hours to the SLA (negative = past it); null when the row has no live SLA. */
  hoursLeft: number | null;
  overdue: boolean;
  /** The review target this row is measured against (for the overdue warning). */
  slaHours: number;
  flags: string[];
  cityId?: string;
  cityLabel?: string;
  categoryId?: string;
  categoryLabel?: string;
  search: string;
  /** Eligible for "Select all clean": waiting, SLA live, no flag (design cleanIds). */
  clean: boolean;
}

export interface Place {
  cityId: string;
  cityLabel: string;
  areaLabel: string;
}

export interface Lookups {
  categories: Map<string, Category>;
  templates: Map<string, Template>;
  places: Map<string, Place>;
  businesses: Map<string, AdminBusiness>;
  businessesBySlug: Map<string, AdminBusiness>;
}

export interface RowContext {
  lang: Lang;
  t: (key: MessageKey) => string;
  lookups: Lookups;
  now: number;
  money: (minor: number) => string;
}

const HOUR = 3_600_000;

export const isPending = (s: RowState) => s === 'waiting' || s === 'waiting_merchant';

export function fmt(ctx: Pick<RowContext, 'lang'>, key: MessageKey, params: MessageParams): string {
  return formatMessage(ctx.lang, key, params);
}

export function dateLabel(lang: Lang, iso: string | undefined | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : formatDate(lang, d);
}

/** Whole hours from now to `dueIso` (rounded; negative once past). */
export function hoursUntil(dueIso: string | undefined | null, now: number): number | null {
  if (!dueIso) return null;
  const due = Date.parse(dueIso);
  return Number.isNaN(due) ? null : Math.round((due - now) / HOUR);
}

export function buildPlaces(nodes: LocationNode[], lang: Lang): Map<string, Place> {
  const out = new Map<string, Place>();
  for (const city of nodes) {
    const cityLabel = lang === 'bn' ? city.name_bn : city.name_en;
    out.set(city.id, { cityId: city.id, cityLabel, areaLabel: cityLabel });
    for (const area of city.children ?? []) {
      out.set(area.id, {
        cityId: city.id,
        cityLabel,
        areaLabel: lang === 'bn' ? area.name_bn : area.name_en,
      });
    }
  }
  return out;
}

export function categoryLabel(ctx: RowContext, id: string | undefined): string {
  if (!id) return '—';
  const c = ctx.lookups.categories.get(id);
  if (!c) return id;
  return ctx.lang === 'bn' ? c.label_bn : c.label_en;
}

export function templateLabel(ctx: RowContext, categoryId: string | undefined): string | null {
  const c = categoryId ? ctx.lookups.categories.get(categoryId) : undefined;
  const tpl = c?.template_id ? ctx.lookups.templates.get(c.template_id) : undefined;
  if (!tpl) return null;
  return ctx.lang === 'bn' ? (tpl.name_bn ?? tpl.name_en) : tpl.name_en;
}

export function docKindLabel(t: RowContext['t'], kind: string): string {
  const known = ['nid', 'trade_license', 'tin', 'credential', 'other'];
  return t(`admin.approvals.doc.kind.${known.includes(kind) ? kind : 'other'}` as MessageKey);
}

export function checkStateLabel(t: RowContext['t'], state: string | null | undefined): string {
  if (state === 'clear' || state === 'unreadable' || state === 'mismatch')
    return t(`admin.approvals.doc.${state}` as MessageKey);
  return t('admin.approvals.doc.unchecked');
}

function search(...parts: (string | undefined | null)[]): string {
  return parts.filter(Boolean).join(' ').toLowerCase();
}

// ---------------------------------------------------------------- business verification

// ---------------------------------------------------------------- business verification (DR-78)

/**
 * A listing waiting for Business verification: every business starts `pending_approval`
 * (createMerchantBusiness API-105, or an admin's Add business API-142) — the For-Business
 * application funnel is retired (DR-78). Decided through the business moderation verb
 * (PATCH /admin/businesses/{id}, audited).
 */
export function rowFromNewBusiness(ctx: RowContext, b: AdminBusiness): QueueRow {
  const place = ctx.lookups.places.get(b.location_id);
  const category = categoryLabel(ctx, b.category_id);
  const state: RowState =
    b.status === 'pending_approval' ? 'waiting' : b.status === 'rejected' ? 'rejected' : 'approved';
  const chips: RowChip[] = [];
  if (b.is_individual)
    chips.push({ text: ctx.t('admin.approvals.chip.individual'), tone: 'violet' });
  const name = ctx.lang === 'bn' && b.name_bn ? b.name_bn : b.name;
  return {
    id: b.id,
    tab: 'merchants',
    title: name,
    chips,
    meta: fmt(ctx, 'admin.approvals.meta.payment', {
      category,
      area: b.area_name ?? place?.areaLabel ?? '—',
    }),
    state,
    // The directory row carries no submission time, so there is no live SLA.
    hoursLeft: null,
    overdue: false,
    slaHours: APPROVAL_SLA.REVIEW_SLA_HOURS,
    flags: [],
    cityId: place?.cityId,
    cityLabel: place?.cityLabel,
    categoryId: b.category_id,
    categoryLabel: category,
    search: search(b.name, b.name_bn, b.area_name, place?.cityLabel, category),
    clean: false,
  };
}

// ---------------------------------------------------------------- services (FR-A19)

function serviceState(item: AdminCatalogItem): RowState | null {
  switch (item.status) {
    case 'pending_review':
      return 'waiting';
    case 'changes_requested':
      return 'waiting_merchant';
    case 'active':
    case 'paused':
      return 'approved';
    case 'rejected':
    case 'suspended':
      return 'rejected';
    default:
      return null; // drafts were never submitted — not an approval record
  }
}

export function rowFromService(ctx: RowContext, item: AdminCatalogItem): QueueRow | null {
  const state = serviceState(item);
  if (!state) return null;
  const biz = ctx.lookups.businesses.get(item.business_id);
  const place = biz ? ctx.lookups.places.get(biz.location_id) : undefined;
  const categoryId = item.category_id ?? biz?.category_id;
  const category = categoryLabel(ctx, categoryId);
  const due = item.submitted_at
    ? new Date(Date.parse(item.submitted_at) + APPROVAL_SLA.REVIEW_SLA_HOURS * HOUR).toISOString()
    : null;
  const hoursLeft = state === 'waiting' ? hoursUntil(due, ctx.now) : null;
  const parentName = item.business_name ?? biz?.name ?? '—';
  const chips: RowChip[] = [];
  if (biz && biz.status !== 'active' && biz.status !== 'suspended') {
    chips.push(
      biz.status === 'rejected' || biz.status === 'banned'
        ? {
            text: fmt(ctx, 'admin.approvals.chip.branch_rejected', { name: parentName }),
            tone: 'danger',
          }
        : {
            text: fmt(ctx, 'admin.approvals.chip.branch_pending', { name: parentName }),
            tone: 'warn',
          },
    );
  } else {
    chips.push({ text: parentName, tone: 'neutral' });
  }
  const tpl = templateLabel(ctx, categoryId);
  if (tpl) chips.push({ text: tpl, tone: 'info' });
  if (state === 'waiting_merchant')
    chips.push({ text: ctx.t('admin.approvals.chip.waiting_merchant'), tone: 'neutral' });
  const price = item.price_on_request
    ? ctx.t('admin.approvals.value.on_request')
    : ctx.money(item.price_minor);
  const name = ctx.lang === 'bn' && item.name_bn ? item.name_bn : item.name_en;
  return {
    id: item.id,
    tab: 'listings',
    title: name,
    chips,
    meta: fmt(ctx, 'admin.approvals.meta.service', {
      price,
      category,
      date: dateLabel(ctx.lang, item.submitted_at),
    }),
    state,
    hoursLeft,
    overdue: hoursLeft !== null && hoursLeft < 0,
    slaHours: APPROVAL_SLA.REVIEW_SLA_HOURS,
    flags: [],
    cityId: place?.cityId,
    cityLabel: place?.cityLabel,
    categoryId,
    categoryLabel: category,
    search: search(item.name_en, item.name_bn, parentName, place?.cityLabel, category),
    clean: false,
  };
}

// ---------------------------------------------------------------- payment verification (FR-A20)

export const PAY_TONE: Record<PaymentKycState, ChipTone> = {
  not_started: 'neutral',
  under_review: 'info',
  approved: 'ok',
  more_info: 'warn',
  rejected: 'danger',
  blocked_individual: 'violet',
};

export function kycState(b: AdminBusiness): PaymentKycState {
  return b.kyc_state ?? b.payment_application_status ?? 'not_started';
}

export function payStateLabel(t: RowContext['t'], s: PaymentKycState): string {
  return t(statusLabelKey('payment_kyc_state', s));
}

function paymentState(s: PaymentKycState): RowState {
  if (s === 'approved') return 'approved';
  if (s === 'rejected') return 'rejected';
  if (s === 'not_started') return 'none';
  return s === 'more_info' ? 'waiting_merchant' : 'waiting';
}

export function rowFromBusiness(ctx: RowContext, b: AdminBusiness): QueueRow {
  const s = kycState(b);
  const place = ctx.lookups.places.get(b.location_id);
  const category = categoryLabel(ctx, b.category_id);
  const flags =
    s === 'blocked_individual'
      ? [ctx.t('admin.approvals.flag.nid')]
      : s === 'more_info'
        ? [ctx.t('admin.approvals.flag.more')]
        : [];
  const chips: RowChip[] = [];
  if (b.is_individual)
    chips.push({ text: ctx.t('admin.approvals.chip.individual'), tone: 'violet' });
  chips.push({ text: payStateLabel(ctx.t, s), tone: PAY_TONE[s] });
  const name = ctx.lang === 'bn' && b.name_bn ? b.name_bn : b.name;
  return {
    id: b.id,
    tab: 'payments',
    title: name,
    chips,
    meta: fmt(ctx, 'admin.approvals.meta.payment', {
      category,
      area: b.area_name ?? place?.areaLabel ?? '—',
    }),
    state: paymentState(s),
    // The directory row carries no submission time, so payment KYC has no live SLA
    // (contract gap reported in DC92); more-info rows still read "Paused".
    hoursLeft: null,
    overdue: false,
    slaHours: APPROVAL_SLA.REVIEW_SLA_HOURS,
    flags,
    cityId: place?.cityId,
    cityLabel: place?.cityLabel,
    categoryId: b.category_id,
    categoryLabel: category,
    search: search(b.name, b.name_bn, b.area_name, place?.cityLabel, category),
    clean: false,
  };
}

// ---------------------------------------------------------------- claims (FR-A17)

function claimState(c: AdminClaim): RowState {
  if (c.status === 'approved') return 'approved';
  if (c.status === 'rejected' || c.status === 'withdrawn') return 'rejected';
  return 'waiting';
}

export function rowFromClaim(ctx: RowContext, c: AdminClaim, slaHours: number): QueueRow {
  const state = claimState(c);
  const biz = ctx.lookups.businessesBySlug.get(c.business_slug);
  const place = biz ? ctx.lookups.places.get(biz.location_id) : undefined;
  const category = categoryLabel(ctx, biz?.category_id);
  const hoursLeft = state === 'waiting' ? slaHours - (c.age_hours ?? 0) : null;
  const claimant = c.claimant?.name ?? c.claimant?.id ?? '—';
  const chips: RowChip[] = [
    { text: ctx.t('admin.approvals.chip.claim'), tone: 'violet' },
    {
      text: ctx.t(
        c.path === 'instant'
          ? 'admin.approvals.chip.path_instant'
          : 'admin.approvals.chip.path_documents',
      ),
      tone: 'neutral',
    },
  ];
  return {
    id: c.id,
    tab: 'claims',
    title: c.business_name ?? c.business_slug,
    chips,
    meta: fmt(ctx, 'admin.approvals.meta.claim', {
      name: claimant,
      phone: formatPhone(c.claimed_phone, phonePlan),
      date: dateLabel(ctx.lang, c.created_at),
    }),
    state,
    hoursLeft,
    // The server owns the overdue call (DR-28) — the console never recomputes it.
    overdue: state === 'waiting' && Boolean(c.overdue),
    slaHours,
    flags: [],
    cityId: place?.cityId,
    cityLabel: place?.cityLabel,
    categoryId: biz?.category_id,
    categoryLabel: category,
    search: search(c.business_name, c.business_slug, claimant, c.claimed_phone, place?.cityLabel),
    clean: false,
  };
}

// ---------------------------------------------------------------- filter + order

export interface RowFilters {
  decided: Decided;
  q: string;
  sla: SlaFilter;
  city: string;
  category: string;
  flagged: boolean;
}

export function matchesFilters(r: QueueRow, f: RowFilters): boolean {
  if (f.decided === 'pending' && !isPending(r.state)) return false;
  if (f.decided === 'approved' && r.state !== 'approved') return false;
  if (f.decided === 'rejected' && r.state !== 'rejected') return false;
  const q = f.q.trim().toLowerCase();
  if (q && !r.search.includes(q)) return false;
  if (f.city !== 'all' && r.cityId !== f.city) return false;
  if (f.category !== 'all' && r.categoryId !== f.category) return false;
  if (
    f.sla === 'due' &&
    !(
      r.state === 'waiting' &&
      !r.overdue &&
      r.hoursLeft !== null &&
      r.hoursLeft <= APPROVAL_SLA.DUE_SOON_HOURS
    )
  )
    return false;
  if (f.sla === 'overdue' && !r.overdue) return false;
  if (f.flagged && r.flags.length === 0) return false;
  return true;
}

/** Waiting work first — overdue on top, then the shortest time left — then decided rows. */
export function sortRows(rows: QueueRow[]): QueueRow[] {
  const rank = (r: QueueRow) => (isPending(r.state) ? 0 : 1);
  return [...rows].sort(
    (a, b) =>
      rank(a) - rank(b) ||
      Number(b.overdue) - Number(a.overdue) ||
      (a.hoursLeft ?? Number.POSITIVE_INFINITY) - (b.hoursLeft ?? Number.POSITIVE_INFINITY),
  );
}

/** Median hours from submission to decision; null without history. */
export function medianHours(
  pairs: readonly [string | undefined, string | undefined][],
): number | null {
  const spans = pairs
    .map(([from, to]) => (from && to ? (Date.parse(to) - Date.parse(from)) / HOUR : Number.NaN))
    .filter((h) => Number.isFinite(h) && h >= 0)
    .sort((a, b) => a - b);
  if (!spans.length) return null;
  const mid = Math.floor(spans.length / 2);
  const m = spans.length % 2 ? spans[mid]! : (spans[mid - 1]! + spans[mid]!) / 2;
  return Math.round(m);
}

// ---------------------------------------------------------------- certificates (FR-A28, DR-83)

function certificateState(c: AdminCertificate): RowState {
  if (c.state === 'ok') return 'approved';
  if (c.state === 'rejected' || c.state === 'expired') return 'rejected';
  return 'waiting';
}

export function certificateKindLabel(ctx: RowContext, categoryId: string | undefined, kind: string): string {
  const c = categoryId ? ctx.lookups.categories.get(categoryId) : undefined;
  const k = c?.certificate_kinds?.find((x) => x.key === kind);
  return k ? (ctx.lang === 'bn' ? k.label_bn : k.label_en) : kind;
}

/** A merchant-uploaded certificate waiting for verification (API-266): the review SLA runs from its submission. */
export function rowFromCertificate(ctx: RowContext, c: AdminCertificate): QueueRow {
  const biz = c.business ? ctx.lookups.businesses.get(c.business.id) : undefined;
  const place = biz ? ctx.lookups.places.get(biz.location_id) : undefined;
  const categoryId = c.business?.category_id ?? biz?.category_id;
  const category = categoryLabel(ctx, categoryId);
  const kind = certificateKindLabel(ctx, categoryId, c.kind);
  const state = certificateState(c);
  const due = c.submitted_at ? new Date(Date.parse(c.submitted_at) + APPROVAL_SLA.REVIEW_SLA_HOURS * HOUR).toISOString() : null;
  const hoursLeft = state === 'waiting' ? hoursUntil(due, ctx.now) : null;
  const chips: RowChip[] = [{ text: kind, tone: 'info' }];
  if (c.state === 'expired') chips.push({ text: ctx.t('lr.admin.cert.state.expired'), tone: 'danger' });
  return {
    id: c.id,
    tab: 'certificates',
    title: c.business?.name ?? biz?.name ?? '—',
    chips,
    meta: fmt(ctx, 'lr.admin.approvals.meta.certificate', { kind, category, date: dateLabel(ctx.lang, c.expires_at) }),
    state,
    hoursLeft,
    overdue: hoursLeft !== null && hoursLeft < 0,
    slaHours: APPROVAL_SLA.REVIEW_SLA_HOURS,
    flags: [],
    cityId: place?.cityId,
    cityLabel: place?.cityLabel,
    categoryId,
    categoryLabel: category,
    search: search(c.business?.name, biz?.name_bn, kind, c.kind, place?.cityLabel, category),
    clean: false,
  };
}
